import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, Plus, Loader2, CheckCircle2, AlertCircle, Coins,
  CreditCard, TrendingUp, Receipt, Settings, History, BarChart3, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// ====================== WALLET WIDGET (header) ======================
export const WalletWidget = ({ authToken, onClickRecharge }) => {
  const [balance, setBalance] = useState(null);

  const load = useCallback(async () => {
    if (!authToken) return;
    try {
      const r = await axios.get(`${API}/wallet/me?token=${authToken}`);
      setBalance(r.data.balance_credits);
    } catch { /* ignore */ }
  }, [authToken]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  // Refresh on custom event (after a render etc.)
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("wallet:refresh", handler);
    return () => window.removeEventListener("wallet:refresh", handler);
  }, [load]);

  // Auto-poll SumUp checkout on return (?wallet_session=...).
  // Kept here in the always-mounted header widget so credits are accredited
  // even if the user never opens the recharge dialog.
  useEffect(() => {
    if (!authToken) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("wallet_session");
    const cancel = params.get("wallet_cancel");
    if (cancel) {
      toast.info("Pagamento annullato");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;

    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/wallet/checkout/status/${sid}?token=${authToken}`);
        if (r.data.payment_status === "paid") {
          toast.success(`Pagamento completato! +${Number(r.data.credits_real).toFixed(2)} Crediti accreditati`);
          window.history.replaceState({}, "", window.location.pathname);
          load();
          window.dispatchEvent(new Event("wallet:refresh"));
          return;
        }
        if (["expired", "failed"].includes(r.data.status)) {
          toast.error("Pagamento non riuscito");
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch { /* retry */ }
      if (attempts < 15) {
        setTimeout(poll, 2000);
      } else {
        toast.warning("Verifica pagamento in corso, ricarica la pagina tra qualche minuto.");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [authToken]);

  if (balance === null) return null;
  const lowBalance = balance < 0.2;

  return (
    <button onClick={onClickRecharge} data-testid="wallet-widget"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
        lowBalance
          ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100"
          : "bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100"
      }`}
      title={lowBalance ? "Saldo basso, ricarica" : "Wallet AI"}>
      <Coins className={`h-4 w-4 ${lowBalance ? "text-amber-600" : "text-emerald-600"}`} />
      <span className="font-mono">{balance.toFixed(2)} Crediti</span>
      {lowBalance && <Plus className="h-3 w-3" />}
    </button>
  );
};


// ====================== RECHARGE DIALOG ======================
export const WalletRechargeDialog = ({ open, onOpenChange, authToken }) => {
  const [packages, setPackages] = useState([]);
  const [markup, setMarkup] = useState(100);
  const [balance, setBalance] = useState(0);
  const [creating, setCreating] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const loadData = useCallback(async () => {
    if (!authToken) return;
    try {
      const [meRes, pkgRes] = await Promise.all([
        axios.get(`${API}/wallet/me?token=${authToken}`),
        axios.get(`${API}/wallet/packages?token=${authToken}`),
      ]);
      setBalance(meRes.data.balance_credits);
      setMarkup(meRes.data.markup_percentage);
      setTransactions(meRes.data.transactions || []);
      setPackages(pkgRes.data.packages || []);
    } catch { /* ignore */ }
  }, [authToken]);

  useEffect(() => { if (open) loadData(); }, [open, loadData]);

  const handleCheckout = async (pkg) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/wallet/checkout?token=${authToken}`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (r.data.url) window.location.href = r.data.url;
      else toast.error("URL checkout mancante");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione checkout");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wallet-recharge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WalletIcon className="h-5 w-5" /> Wallet AI</DialogTitle>
          <DialogDescription>Ricarica il tuo saldo per utilizzare le funzioni AI (Render, Inventario, Analisi).</DialogDescription>
        </DialogHeader>

        {/* Saldo */}
        <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-gray-600">Saldo corrente</div>
                <div className="text-3xl font-bold text-indigo-700 font-mono" data-testid="wallet-balance">{balance.toFixed(2)} <span className="text-base font-medium">Crediti</span></div>
              </div>
              <div className="text-right text-xs text-gray-600">
                <div>Listino corrente con +{markup}% di markup</div>
                <div>Render base: {(0.04 * (1 + markup/100)).toFixed(3)} Crediti</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pacchetti */}
        <div className="space-y-2">
          <h3 className="font-semibold text-sm">Pacchetti disponibili</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {packages.map(pkg => (
              <Card key={pkg.id} className="border-2 hover:border-indigo-400 transition" data-testid={`wallet-pkg-${pkg.id}`}>
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-lg">{pkg.label}</CardTitle>
                  <CardDescription>{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-bold text-emerald-700 mb-2">
                    {pkg.currency === "eur" ? "€" : "$"}{pkg.stripe_amount.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mb-3">
                    Ricevi <strong>{pkg.credits_real.toFixed(2)} Crediti</strong> AI (≈ {Math.floor(pkg.credits_real / (0.04 * (1 + markup/100)))} render)
                  </div>
                  <Button onClick={() => handleCheckout(pkg)} disabled={creating} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid={`wallet-buy-${pkg.id}`}>
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1" /> Acquista</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
            {packages.length === 0 && <div className="col-span-full text-center text-sm text-gray-500 py-4">Nessun pacchetto configurato</div>}
          </div>
        </div>

        {/* Cronologia */}
        {transactions.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold text-sm flex items-center gap-1"><History className="h-4 w-4" /> Ultime transazioni</h3>
            <div className="border rounded-lg overflow-y-auto max-h-60">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1">Data</th>
                    <th className="px-2 py-1">Tipo</th>
                    <th className="px-2 py-1">Descrizione</th>
                    <th className="px-2 py-1 text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 30).map(tx => (
                    <tr key={tx.id} className="border-t">
                      <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{new Date(tx.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-2 py-1"><Badge variant={tx.amount > 0 ? "default" : "secondary"} className="text-[10px]">{tx.kind}</Badge></td>
                      <td className="px-2 py-1 text-gray-700">{tx.description}</td>
                      <td className={`px-2 py-1 text-right font-mono font-medium ${tx.amount > 0 ? "text-emerald-700" : "text-red-600"}`}>{tx.amount > 0 ? "+" : ""}{tx.amount.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ====================== ADMIN CONFIG & REPORT ======================
export const WalletAdminPanel = ({ authToken }) => {
  const [config, setConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [users, setUsers] = useState([]);
  const [provider, setProvider] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [creditDialog, setCreditDialog] = useState({ open: false, user: null });

  const load = useCallback(async () => {
    if (!authToken) return;
    try {
      const [cfgR, rR, uR, pR] = await Promise.all([
        axios.get(`${API}/wallet/admin/config?token=${authToken}`),
        axios.get(`${API}/wallet/admin/report?token=${authToken}`),
        axios.get(`${API}/wallet/admin/users?token=${authToken}`),
        axios.get(`${API}/wallet/admin/payment-provider?token=${authToken}`),
      ]);
      setConfig(cfgR.data);
      setReport(rR.data);
      setUsers(uR.data || []);
      setProvider(pR.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/wallet/admin/config?token=${authToken}`, {
        markup_percentage: parseFloat(config.markup_percentage),
        packages: config.packages,
      });
      toast.success("Configurazione salvata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSavingCfg(false); }
  };

  const setMarkup = (v) => setConfig(prev => ({ ...prev, markup_percentage: v }));
  const updatePkg = (i, field, v) => setConfig(prev => ({
    ...prev,
    packages: prev.packages.map((p, idx) => idx === i ? { ...p, [field]: v } : p)
  }));
  const addPkg = () => setConfig(prev => ({
    ...prev,
    packages: [...prev.packages, { id: `pkg-${Date.now()}`, label: "Nuovo", credits_real: 1, stripe_amount: 2, currency: "eur", description: "" }]
  }));
  const removePkg = (i) => setConfig(prev => ({ ...prev, packages: prev.packages.filter((_, idx) => idx !== i) }));

  const handleManualCredit = async (amount, note) => {
    if (!creditDialog.user) return;
    try {
      await axios.post(`${API}/wallet/admin/credit/${creditDialog.user.id}?token=${authToken}`, { amount: parseFloat(amount), note });
      toast.success("Saldo aggiornato");
      setCreditDialog({ open: false, user: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleReconcile = async () => {
    try {
      const r = await axios.post(`${API}/wallet/admin/reconcile?token=${authToken}`);
      if (r.data.reconciled_count > 0) {
        toast.success(`Riconciliati ${r.data.reconciled_count} pagamenti SumUp`);
      } else {
        toast.info(`Nessun pagamento da riconciliare (${r.data.checked} verificati)`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore riconciliazione");
    }
  };

  if (!config || !report) return <div className="text-center py-6"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2 text-emerald-700"><WalletIcon className="h-6 w-6" /> Gestione Wallet AI</h2>
        <p className="text-sm text-gray-500">Configura markup, pacchetti e monitora il fatturato dei crediti AI</p>
      </div>

      <Tabs defaultValue="report">
        <TabsList>
          <TabsTrigger value="report" data-testid="wallet-adm-tab-report"><BarChart3 className="h-4 w-4 mr-1" /> Report</TabsTrigger>
          <TabsTrigger value="config" data-testid="wallet-adm-tab-config"><Settings className="h-4 w-4 mr-1" /> Configurazione</TabsTrigger>
          <TabsTrigger value="users" data-testid="wallet-adm-tab-users"><Receipt className="h-4 w-4 mr-1" /> Utenti</TabsTrigger>
        </TabsList>

        {/* REPORT */}
        <TabsContent value="report" className="space-y-3 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <div className="text-xs text-emerald-700">Markup accumulato (credito AI)</div>
                <div className="text-3xl font-bold text-emerald-800 font-mono">${report.total_markup_earned.toFixed(3)}</div>
                <div className="text-xs text-emerald-600">{report.markup_transactions_count} transazioni</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="text-xs text-blue-700">Fatturato Stripe</div>
                <div className="text-3xl font-bold text-blue-800 font-mono">€{report.total_stripe_revenue.toFixed(2)}</div>
                <div className="text-xs text-blue-600">{report.paid_transactions_count} pagamenti</div>
              </CardContent>
            </Card>
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="p-4">
                <div className="text-xs text-indigo-700">Markup % corrente</div>
                <div className="text-3xl font-bold text-indigo-800 font-mono">{config.markup_percentage}%</div>
                <div className="text-xs text-indigo-600">Su ogni operazione AI</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Top utenti per spesa</CardTitle></CardHeader>
            <CardContent>
              {report.per_user_spend.length === 0 ? (
                <p className="text-sm text-gray-500">Nessuna spesa registrata.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs">
                    <tr><th className="p-2">Utente</th><th className="p-2">Email</th><th className="p-2 text-right">Speso</th><th className="p-2 text-right">Operazioni</th></tr>
                  </thead>
                  <tbody>
                    {report.per_user_spend.map(r => (
                      <tr key={r.user_id} className="border-t">
                        <td className="p-2 font-medium">{r.username}</td>
                        <td className="p-2 text-gray-600">{r.email}</td>
                        <td className="p-2 text-right font-mono text-red-700">${r.total_spent.toFixed(3)}</td>
                        <td className="p-2 text-right">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFIG */}
        <TabsContent value="config" className="space-y-3 mt-3">
          {/* Payment Provider — SumUp */}
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-emerald-700" /> Provider Pagamenti
                {provider?.configured && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">OPERATIVO</Badge>}
                {!provider?.configured && <Badge variant="destructive" className="text-[10px]">NON CONFIGURATO</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-600">Provider</span><span className="font-mono font-medium uppercase">SumUp</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Merchant code</span><span className="font-mono">{provider?.merchant_code || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">API key</span><span className="font-mono text-xs">{provider?.api_key_masked || "—"}</span></div>
              {provider?.live_info && (
                <>
                  <div className="flex justify-between"><span className="text-gray-600">Intestatario</span><span className="font-medium">{provider.live_info.company_name || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Email</span><span className="text-xs">{provider.live_info.email || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Paese</span><span>{provider.live_info.country || "—"}</span></div>
                </>
              )}
              <p className="text-[11px] text-gray-500 pt-2 border-t mt-2">
                Chiavi gestite in <code>backend/.env</code> (<code>SUMUP_API_KEY</code>, <code>SUMUP_MERCHANT_CODE</code>). I pagamenti utilizzano <strong>SumUp Hosted Checkout</strong>: l&apos;utente viene reindirizzato alla pagina sicura SumUp e, al rientro, i crediti vengono accreditati automaticamente.
              </p>
              <Button onClick={handleReconcile} size="sm" variant="outline" className="mt-2" data-testid="wallet-adm-reconcile">
                <History className="h-4 w-4 mr-1" /> Riconcilia pagamenti SumUp pendenti
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Markup</CardTitle></CardHeader>
            <CardContent>
              <Label>Percentuale markup (0-500%)</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="500" step="5" value={config.markup_percentage} onChange={e => setMarkup(e.target.value)} className="max-w-xs" data-testid="wallet-adm-markup" />
                <span className="text-sm text-gray-600">% (es. 100 = utente paga 2x, admin trattiene la metà)</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Con {config.markup_percentage}% di markup, un render reale da $0.04 verrà addebitato all&apos;utente come <strong>${(0.04 * (1 + config.markup_percentage/100)).toFixed(3)}</strong>, di cui ${(0.04 * config.markup_percentage/100).toFixed(3)} restano all&apos;admin.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Pacchetti</CardTitle>
                <Button size="sm" variant="outline" onClick={addPkg}><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
            </CardHeader>
            <CardContent>
              {config.packages.map((p, i) => (
                <div key={i} className="border rounded p-2 mb-2 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                  <div><Label className="text-[10px]">ID</Label><Input value={p.id} onChange={e => updatePkg(i, "id", e.target.value)} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px]">Label</Label><Input value={p.label} onChange={e => updatePkg(i, "label", e.target.value)} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px]">Crediti AI</Label><Input type="number" step="0.5" value={p.credits_real} onChange={e => updatePkg(i, "credits_real", parseFloat(e.target.value)||0)} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px]">Prezzo (€)</Label><Input type="number" step="0.5" value={p.stripe_amount} onChange={e => updatePkg(i, "stripe_amount", parseFloat(e.target.value)||0)} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px]">Currency</Label><Input value={p.currency} onChange={e => updatePkg(i, "currency", e.target.value.toLowerCase())} className="h-8 text-xs" /></div>
                  <div className="flex gap-1">
                    <Input value={p.description} onChange={e => updatePkg(i, "description", e.target.value)} className="h-8 text-xs flex-1" placeholder="descr." />
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removePkg(i)}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Button onClick={handleSaveConfig} disabled={savingCfg} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wallet-adm-save">
            {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva configurazione"}
          </Button>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Utenti & Saldi</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs">
                  <tr><th className="p-2">Utente</th><th className="p-2">Email</th><th className="p-2">Ruolo</th><th className="p-2 text-right">Saldo</th><th className="p-2 text-right">Ricaricato</th><th className="p-2 text-right">Speso</th><th className="p-2"></th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t">
                      <td className="p-2 font-medium">{u.username}</td>
                      <td className="p-2 text-gray-600">{u.email}</td>
                      <td className="p-2"><Badge variant="secondary" className="text-[10px]">{u.role || "user"}</Badge></td>
                      <td className="p-2 text-right font-mono text-emerald-700">${u.balance_credits.toFixed(3)}</td>
                      <td className="p-2 text-right font-mono text-gray-600">${u.total_topup.toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-red-600">${u.total_spent.toFixed(3)}</td>
                      <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => setCreditDialog({ open: true, user: u })}><Plus className="h-3.5 w-3.5 mr-1" /> Crediti</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ManualCreditDialog
        open={creditDialog.open}
        onOpenChange={(o) => setCreditDialog(prev => ({ ...prev, open: o }))}
        user={creditDialog.user}
        onSubmit={handleManualCredit}
      />
    </div>
  );
};


const ManualCreditDialog = ({ open, onOpenChange, user, onSubmit }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => { if (open) { setAmount(""); setNote(""); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accredita/Addebita saldo</DialogTitle>
          <DialogDescription>Utente: <strong>{user?.username}</strong> ({user?.email})</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Importo (positivo = accredita, negativo = addebita)</Label>
          <Input type="number" step="0.5" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Es. 5 oppure -2" data-testid="manual-credit-amount" />
          <Label>Nota</Label>
          <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Es. Bonifico ricevuto" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => onSubmit(amount, note)} disabled={!amount} data-testid="manual-credit-submit">Conferma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
